import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

const WEEKDAYS = [ "Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat" ]

export default class extends Controller {
  static targets = [ "title", "body", "modal", "since" ]
  static values = {
    appointments: { type: Array, default: [] },
    canBook: { type: Boolean, default: false }
  }

  connect() {
    this.currentDate = new Date()
    this.view = "month"
    if (this.hasModalTarget) this.modalInstance = Modal.getOrCreateInstance(this.modalTarget)
    this.render()
  }

  disconnect() {
    this.modalInstance?.dispose()
  }

  previous() {
    this.movePeriod(-1)
  }

  next() {
    this.movePeriod(1)
  }

  today() {
    this.currentDate = new Date()
    this.render()
  }

  showMonth() {
    this.view = "month"
    this.render()
  }

  showWeek() {
    this.view = "week"
    this.render()
  }

  openNew() {
    this.openForDate(this.toISODate(new Date()))
  }

  openForDate(isoDate) {
    if (!this.canBookValue || !this.hasModalTarget) return

    if (this.hasSinceTarget) {
      this.sinceTarget.value = this.defaultSinceValue(isoDate)
      this.sinceTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }

    this.modalInstance.show()
  }

  movePeriod(direction) {
    const date = new Date(this.currentDate)
    if (this.view === "week") date.setDate(date.getDate() + direction * 7)
    else date.setMonth(date.getMonth() + direction)
    this.currentDate = date
    this.render()
  }

  render() {
    if (this.hasTitleTarget) this.titleTarget.textContent = this.titleText()
    if (!this.hasBodyTarget) return

    this.bodyTarget.replaceChildren()
    this.bodyTarget.append(this.view === "week" ? this.renderWeekView() : this.renderMonthView())
    this.syncViewButtons()
  }

  titleText() {
    if (this.view === "week") {
      const start = this.startOfWeek(this.currentDate)
      const end = this.addDays(start, 6)
      return `${this.formatShortDate(start)} – ${this.formatShortDate(end)}`
    }

    return this.currentDate.toLocaleDateString("en", { month: "long", year: "numeric" })
  }

  syncViewButtons() {
    this.element.querySelectorAll("[data-calendar-view]").forEach((button) => {
      button.classList.toggle("active", button.dataset.calendarView === this.view)
    })
  }

  renderMonthView() {
    const fragment = document.createDocumentFragment()
    fragment.append(this.renderWeekdays())

    const grid = document.createElement("div")
    grid.className = "nw-calendar__grid"

    const monthStart = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1)
    const start = this.startOfWeek(monthStart)

    for (let index = 0; index < 42; index += 1) {
      const date = this.addDays(start, index)
      grid.append(this.createDayCell(date, {
        isOtherMonth: date.getMonth() !== this.currentDate.getMonth(),
        limit: 3
      }))
    }

    fragment.append(grid)
    return fragment
  }

  renderWeekView() {
    const fragment = document.createDocumentFragment()
    fragment.append(this.renderWeekdays())

    const grid = document.createElement("div")
    grid.className = "nw-calendar__grid nw-calendar__grid--week"
    const start = this.startOfWeek(this.currentDate)

    for (let index = 0; index < 7; index += 1) {
      grid.append(this.createDayCell(this.addDays(start, index), { isWeekView: true }))
    }

    fragment.append(grid)
    return fragment
  }

  renderWeekdays() {
    const weekdays = document.createElement("div")
    weekdays.className = "nw-calendar__weekdays"
    WEEKDAYS.forEach((label) => {
      const item = document.createElement("div")
      item.className = "nw-calendar__weekday"
      item.textContent = label
      weekdays.append(item)
    })
    return weekdays
  }

  createDayCell(date, { isOtherMonth = false, isWeekView = false, limit = null } = {}) {
    const iso = this.toISODate(date)
    const day = document.createElement("div")
    day.dataset.date = iso
    day.tabIndex = 0
    day.setAttribute("role", "button")
    day.setAttribute("aria-label", date.toLocaleDateString("en", { month: "long", day: "numeric" }))
    day.className = [
      "nw-calendar__day",
      isWeekView ? "nw-calendar__day--week" : "",
      isOtherMonth ? "nw-calendar__day--other" : "",
      this.isSameDay(date, new Date()) ? "nw-calendar__day--today" : "",
      date.getDay() === 0 || date.getDay() === 6 ? "nw-calendar__day--weekend" : ""
    ].filter(Boolean).join(" ")
    day.addEventListener("click", () => this.openForDate(iso))
    day.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return
      event.preventDefault()
      this.openForDate(iso)
    })

    const top = document.createElement("div")
    top.className = "nw-calendar__day-top"
    const number = document.createElement("span")
    number.className = "nw-calendar__day-number"
    number.textContent = String(date.getDate())
    top.append(number)

    const eventsEl = document.createElement("div")
    eventsEl.className = "nw-calendar__day-events"

    const events = this.eventsForDate(iso)
    const visible = limit == null ? events : events.slice(0, limit)
    visible.forEach((event) => eventsEl.append(this.renderEventPill(event, isWeekView)))

    if (limit != null && events.length > limit) {
      const more = document.createElement("div")
      more.className = "nw-calendar__more"
      more.textContent = `+${events.length - limit} more`
      eventsEl.append(more)
    }

    day.append(top, eventsEl)
    return day
  }

  renderEventPill(event, showTime = false) {
    const pill = document.createElement("a")
    pill.href = event.url
    pill.className = "nw-calendar__event nw-calendar__event--client"
    pill.addEventListener("click", (clickEvent) => clickEvent.stopPropagation())

    const time = document.createElement("span")
    time.className = "nw-calendar__event-time"
    time.textContent = this.formatTime(event.time)

    const title = document.createElement("span")
    title.className = "nw-calendar__event-title"
    title.textContent = event.title

    if (showTime) pill.append(title, time)
    else pill.append(time, title)

    return pill
  }

  eventsForDate(iso) {
    return this.appointmentsValue
      .filter((event) => event.date === iso)
      .sort((left, right) => String(left.time).localeCompare(String(right.time)))
  }

  defaultSinceValue(isoDate) {
    const [ year, month, day ] = isoDate.split("-").map(Number)
    const now = new Date()
    const date = new Date(year, month - 1, day, 10, 0, 0)

    if (this.isSameDay(date, now)) {
      const nextHour = new Date(now)
      nextHour.setMinutes(0, 0, 0)
      nextHour.setHours(nextHour.getHours() + 1)
      return this.toLocalValue(nextHour)
    }

    return this.toLocalValue(date)
  }

  toLocalValue(date) {
    const pad = (value) => String(value).padStart(2, "0")
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
  }

  toISODate(date) {
    const pad = (value) => String(value).padStart(2, "0")
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  }

  startOfWeek(date) {
    const start = new Date(date)
    start.setHours(12, 0, 0, 0)
    start.setDate(start.getDate() - start.getDay())
    return start
  }

  addDays(date, amount) {
    const next = new Date(date)
    next.setDate(next.getDate() + amount)
    return next
  }

  isSameDay(left, right) {
    return left.getFullYear() === right.getFullYear() &&
      left.getMonth() === right.getMonth() &&
      left.getDate() === right.getDate()
  }

  formatShortDate(date) {
    return date.toLocaleDateString("en", { month: "short", day: "numeric" })
  }

  formatTime(value) {
    const [ hours, minutes ] = String(value || "00:00").split(":")
    const date = new Date()
    date.setHours(Number(hours), Number(minutes || 0), 0, 0)
    return date.toLocaleTimeString("en", { hour: "numeric", minute: "2-digit" })
  }
}
